import { useEffect, useRef, useState } from "react";
import { ShieldAlert } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { cachedEventLocation, captureEventLocation } from "@/lib/location-capture";
import { ensureLocationConsent } from "@/lib/location-consent";
import { recordLocationEvent } from "@/lib/location.functions";

type Flags = {
  sos_enabled: boolean;
  sos_hold_seconds: number;
  sos_double_tap_required: boolean;
  sos_emergency_services_enabled: boolean;
};

const EMERGENCY_HOLD_SECONDS = 5;
const DOUBLE_TAP_WINDOW_MS = 600;

export function SosButton({ compact = false }: { compact?: boolean }) {
  const { currentOrg, user } = useAuth();
  const [flags, setFlags] = useState<Flags | null>(null);
  const [holding, setHolding] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1
  const [submitting, setSubmitting] = useState(false);
  const lastTapRef = useRef<number>(0);
  const startedAtRef = useRef<number>(0);
  const rafRef = useRef<number | null>(null);
  const triggeredRef = useRef<boolean>(false);
  const targetSecondsRef = useRef<number>(3);

  useEffect(() => {
    if (!currentOrg) return;
    supabase
      .from("org_feature_flags")
      .select("sos_enabled, sos_hold_seconds, sos_double_tap_required, sos_emergency_services_enabled")
      .eq("organization_id", currentOrg.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) setFlags(data as Flags);
      });
  }, [currentOrg]);

  const fire = async (severity: "staff" | "emergency", method: "hold" | "double_tap") => {
    if (!currentOrg || !user || submitting) return;
    setSubmitting(true);
    // SOS goes out immediately with whatever position is already known — it is
    // never delayed waiting on the device. A fresh fix follows and is attached
    // to the alert as soon as it arrives.
    const known = cachedEventLocation();
    const { data: inserted, error } = await supabase
      .from("sos_alerts")
      .insert({
        organization_id: currentOrg.id,
        activator_id: user.id,
        severity,
        status: "active",
        trigger_method: method,
        latitude: known.latitude,
        longitude: known.longitude,
        accuracy_m: known.accuracyM,
        location_status: known.status,
        location_captured_at: known.capturedAt,
      })
      .select("id")
      .single();
    setSubmitting(false);
    if (error) {
      toast.error(`SOS failed: ${error.message}`);
      return;
    }
    toast.success(severity === "emergency" ? "EMERGENCY SOS sent" : "SOS sent — supervisors notified");

    void (async () => {
      const orgId = currentOrg.id;
      const consented = await ensureLocationConsent(orgId);
      if (!consented) return;
      const fresh = await captureEventLocation({ timeoutMs: 10000 });
      if (fresh.latitude == null || fresh.longitude == null) return;
      await supabase
        .from("sos_alerts")
        .update({
          latitude: fresh.latitude,
          longitude: fresh.longitude,
          accuracy_m: fresh.accuracyM,
          location_status: fresh.status,
          location_captured_at: fresh.capturedAt,
        })
        .eq("id", inserted?.id ?? "");
      try {
        await recordLocationEvent({
          data: {
            orgId,
            source: "sos",
            sourceId: inserted?.id ?? null,
            latitude: fresh.latitude,
            longitude: fresh.longitude,
            accuracyM: fresh.accuracyM,
            capturedAt: fresh.capturedAt,
            status: fresh.status,
          },
        });
      } catch (err) {
        if (import.meta.env.DEV) console.error("[sos] location event failed", err);
      }
    })();
  };

  const cancelHold = () => {
    setHolding(false);
    setProgress(0);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  };

  const tickProgress = () => {
    const elapsed = (performance.now() - startedAtRef.current) / 1000;
    const target = targetSecondsRef.current;
    const p = Math.min(elapsed / target, 1);
    setProgress(p);
    if (p >= 1 && !triggeredRef.current) {
      triggeredRef.current = true;
      const severity = target >= EMERGENCY_HOLD_SECONDS ? "emergency" : "staff";
      fire(severity, "hold");
      cancelHold();
      return;
    }
    rafRef.current = requestAnimationFrame(tickProgress);
  };

  const handlePressStart = () => {
    if (!flags?.sos_enabled || submitting) return;
    // Double-tap → emergency mode (longer hold to confirm)
    const now = Date.now();
    const isDoubleTap = now - lastTapRef.current < DOUBLE_TAP_WINDOW_MS;
    lastTapRef.current = now;

    if (flags.sos_double_tap_required && !isDoubleTap) {
      // Require a double-tap before any hold can fire
      toast.info("Tap again, then hold to send SOS");
      return;
    }

    targetSecondsRef.current = isDoubleTap ? EMERGENCY_HOLD_SECONDS : flags.sos_hold_seconds;
    triggeredRef.current = false;
    startedAtRef.current = performance.now();
    setHolding(true);
    setProgress(0);
    rafRef.current = requestAnimationFrame(tickProgress);
  };

  const handlePressEnd = () => {
    if (!triggeredRef.current && holding) {
      toast.info("SOS cancelled");
    }
    cancelHold();
  };

  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);

  if (!flags?.sos_enabled) return null;

  const label = holding
    ? targetSecondsRef.current >= EMERGENCY_HOLD_SECONDS
      ? `Hold for EMERGENCY… ${(targetSecondsRef.current - (targetSecondsRef.current * progress)).toFixed(1)}s`
      : `Hold to send SOS… ${(targetSecondsRef.current - (targetSecondsRef.current * progress)).toFixed(1)}s`
    : compact ? "SOS" : "Hold to send SOS";

  return (
    <button
      type="button"
      onMouseDown={handlePressStart}
      onMouseUp={handlePressEnd}
      onMouseLeave={handlePressEnd}
      onTouchStart={(e) => { e.preventDefault(); handlePressStart(); }}
      onTouchEnd={(e) => { e.preventDefault(); handlePressEnd(); }}
      onTouchCancel={handlePressEnd}
      onContextMenu={(e) => e.preventDefault()}
      disabled={submitting}
      className={`relative overflow-hidden select-none rounded-full font-semibold text-white shadow-lg transition-transform active:scale-95 disabled:opacity-60 ${
        compact ? "h-10 px-3 text-xs" : "h-14 px-6 text-sm"
      } ${holding ? "bg-destructive ring-4 ring-destructive/30" : "bg-destructive/90 hover:bg-destructive"}`}
      aria-label="Send SOS"
    >
      <span
        className="absolute inset-0 origin-left bg-white/20"
        style={{ transform: `scaleX(${progress})`, transition: holding ? "none" : "transform 200ms" }}
        aria-hidden
      />
      <span className="relative inline-flex items-center gap-2">
        <ShieldAlert className="h-4 w-4" />
        {label}
      </span>
    </button>
  );
}
